import { supabase } from '../supabaseClient'
import type { OrgRole } from '../utilities/orgRole'
import { networksOfNodes, type NetworkAttachFacts } from '../utilities/registerNetworks'

export interface AttachFacts {
  networks: Map<string, NetworkAttachFacts>
  roles: Map<string, OrgRole>
}

const EMPTY: AttachFacts = { networks: new Map(), roles: new Map() }

/**
 * What the registration page needs to know to offer only the networks the reader may add a node to
 * (app#397): each listed network's owner and "members may add nodes" setting, and the reader's
 * role in each org.
 *
 * ⚠ Plain reads, and every failure is an EMPTY answer. A fact the page does not have keeps the
 * network listed (`addableSession`), and the router's approval stays the gate. So a database
 * without `networks.members_may_add_nodes` (before router migration 0230) fails this select as a
 * whole, and the page lists every network, as it did before the setting existed.
 */
export const fetchAttachFacts = async (networkIds: string[], userId: string | null): Promise<AttachFacts> => {
  if (!userId || networkIds.length === 0) return EMPTY
  try {
    const [networks, members, nodes] = await Promise.all([
      supabase.from('networks').select('id, owner_id, members_may_add_nodes').in('id', networkIds),
      supabase.from('organization_members').select('org_id, role').eq('user_id', userId),
      // The reader's own nodes in the listed networks: the router lets them replace one of those
      // on a closed network, so such a network stays listed (`readerOwnsNode`).
      // ⚠ Through `node_networks`: `nodes.network_id` names one network, and it goes with router#2142.
      supabase
        .from('nodes')
        .select('node_networks!inner(network_id)')
        .eq('owner_user_id', userId)
        .in('node_networks.network_id', networkIds)
        .is('deleted_at', null),
    ])
    const facts: AttachFacts = { networks: new Map(), roles: new Map() }
    // Null when the read failed: not known, so no network is hidden for want of it.
    let owned: Set<string> | null = null
    if (nodes.error) console.warn('register: cannot read your nodes', nodes.error)
    else owned = networksOfNodes((nodes.data ?? []) as unknown as { node_networks: { network_id: string }[] }[])
    if (networks.error) console.warn('register: cannot read the network settings', networks.error)
    else
      for (const row of (networks.data ?? []) as {
        id: string
        owner_id: string | null
        members_may_add_nodes?: boolean | null
      }[])
        facts.networks.set(row.id, {
          ownerId: row.owner_id ?? null,
          membersMayAddNodes: typeof row.members_may_add_nodes === 'boolean' ? row.members_may_add_nodes : null,
          readerOwnsNode: owned ? owned.has(row.id) : null,
        })
    if (members.error) console.warn('register: cannot read the org roles', members.error)
    else
      for (const row of (members.data ?? []) as { org_id: string; role: string }[])
        if (row.role === 'owner' || row.role === 'admin' || row.role === 'member') facts.roles.set(row.org_id, row.role)
    return facts
  } catch (error) {
    console.warn('register: cannot read what decides who adds nodes', error)
    return EMPTY
  }
}
